import { Link, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { useEffect } from "react";
import { lockScroll, unlockScroll } from "@/utils/responsive";

/**
 * Mobile slide-out navigation menu
 * Appears when hamburger menu is clicked on mobile devices
 */
const MobileMenu = ({ isOpen, onClose, role, isAdmin, isHunter, isFarmer, isHeadFarmer, isHeadHunter }) => {
  const location = useLocation();

  // Lock scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      lockScroll();
    } else {
      unlockScroll();
    }
    return () => unlockScroll();
  }, [isOpen]);

  // Close menu when route changes
  useEffect(() => {
    if (isOpen) {
      onClose();
    }
  }, [location.pathname]);

  if (!isOpen) return null;

  // Page type flags (same logic as PageHeader)
  const isLead =
    [
      "/lead_inventory/yours",
      "/lead_inventory/all",
      "/lead_inventory/shark_tank",
    ].includes(location.pathname) || location.pathname === "/lead_journey";

  const isAccDistribution = [
    "/account_distribution/incoming",
    "/account_distribution/assigned",
  ].includes(location.pathname);

  const isAccInventory =
    ["/account_inventory/yours", "/account_inventory/all"].includes(
      location.pathname
    ) || location.pathname === "/account_details";

  const isOpportunity = [
    "/opportunity_inventory/yours",
    "/opportunity_inventory/all",
  ].includes(location.pathname);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[60] lg:hidden"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-out menu */}
      <div className="fixed inset-y-0 left-0 w-[280px] bg-white shadow-2xl z-[70] lg:hidden overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">Menu</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close menu"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="p-4">
          <ul className="space-y-2">
            {/* Home */}
            <li>
              <Link
                to="/home"
                className={`block px-4 py-3 rounded-lg font-semibold transition-colors ${
                  location.pathname === "/home"
                    ? "bg-[#F7E21E] text-[#1E293B]"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                Home
              </Link>
            </li>

            {/* Leads (Hunter/Admin only) */}
            {(isHunter || isAdmin) && (
              <li>
                <Link
                  to="/lead_inventory/yours"
                  className={`block px-4 py-3 rounded-lg font-semibold transition-colors ${
                    isLead
                      ? "bg-[#F7E21E] text-[#1E293B]"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  Leads
                </Link>
              </li>
            )}

            {/* Farmer/Admin sections */}
            {(isFarmer || isAdmin) && (
              <>
                {/* Accounts Distribution (Head Farmer/Admin only) */}
                {(isHeadFarmer || isAdmin) && (
                  <li>
                    <Link
                      to="/account_distribution/incoming"
                      className={`block px-4 py-3 rounded-lg font-semibold transition-colors ${
                        isAccDistribution
                          ? "bg-[#F7E21E] text-[#1E293B]"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      Accounts Distribution
                    </Link>
                  </li>
                )}

                {/* Accounts */}
                <li>
                  <Link
                    to="/account_inventory/yours"
                    className={`block px-4 py-3 rounded-lg font-semibold transition-colors ${
                      isAccInventory
                        ? "bg-[#F7E21E] text-[#1E293B]"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    Accounts
                  </Link>
                </li>

                {/* Opportunities */}
                <li>
                  <Link
                    to="/opportunity_inventory/yours"
                    className={`block px-4 py-3 rounded-lg font-semibold transition-colors ${
                      isOpportunity
                        ? "bg-[#F7E21E] text-[#1E293B]"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    Opportunities
                  </Link>
                </li>
              </>
            )}

            {/* Settings */}
            {(isAdmin || isHeadFarmer || isHeadHunter) && (
              <li>
                <Link
                  to="/settings"
                  className={`block px-4 py-3 rounded-lg font-semibold transition-colors ${
                    location.pathname === "/settings"
                      ? "bg-[#F7E21E] text-[#1E293B]"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  Settings
                </Link>
              </li>
            )}

            {/* Admin */}
            {isAdmin && (
              <li>
                <Link
                  to="/admin"
                  className={`block px-4 py-3 rounded-lg font-semibold transition-colors ${
                    location.pathname === "/admin"
                      ? "bg-[#F7E21E] text-[#1E293B]"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  Admin
                </Link>
              </li>
            )}
          </ul>
        </nav>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-gray-50">
          <p className="text-xs text-gray-500 text-center">
            CSP 4.0 Mobile
          </p>
        </div>
      </div>
    </>
  );
};

export default MobileMenu;

